import { Box, Grid, Paper } from "@mui/material";
import { forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import FormInputText from "../components/Form/FormInputText";
import FormSelection from "../components/Form/FormSelection";
import { CompanyStore } from "../models/company.store.model";
import { Company } from "../models/company.model";
import { useConfig } from "./ConfigContext";
import FormDate from "../components/Form/FormDate";

export default forwardRef(
  (
    props: {
      defaultValues?: Partial<CompanyStore>;
      isModal?: boolean;
    },
    ref: any
  ) => {
    const methods = useForm<CompanyStore>({
      defaultValues: props.defaultValues,
    });
    const { handleSubmit, control, setError } = methods;
    const { loggedUser } = useConfig();

    useImperativeHandle(ref, () => ({
      onSubmit() {
        return new Promise((resolve, reject) => {
          handleSubmit((data) => {
            if (loggedUser?.company) {
              data.company = loggedUser.company;
            }
            resolve(data);
          }, reject)();
        });
      },
    }));

    const getOptionsByCompanyId = (key: keyof Company) => {
      const options = loggedUser?.company?.[key]
        ?.toString()
        .split(",")
        .map((a) => ({ label: a, value: a }));
      return (
        options || [
          { label: "Failed to load", value: "no_value", disabled: true },
        ]
      );
    };

    return (
      <Box
        component="form"
        sx={{
          "& .MuiTextField-root": { mt: 1, mb: 1 },
        }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper className="padding-big">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormInputText
                    name="name"
                    control={control}
                    setError={setError}
                    label="Store Name"
                    required
                    rules={{ required: "Store Name is required" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputText
                    name="storeId"
                    control={control}
                    setError={setError}
                    label="Store ID"
                    required
                    rules={{ required: "Store ID is required" }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper elevation={3} className="padding-big">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormSelection
                    name="storeType"
                    control={control}
                    setError={setError}
                    label="Store Type"
                    noEmpty
                    options={getOptionsByCompanyId("storeType")}
                    required
                    rules={{ required: "Store Type is required" }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormSelection
                    name="area"
                    control={control}
                    setError={setError}
                    label="Area"
                    noEmpty
                    options={getOptionsByCompanyId("area")}
                    required
                    rules={{ required: "Area is required" }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormSelection
                    name="region"
                    control={control}
                    setError={setError}
                    label="Region"
                    noEmpty
                    options={getOptionsByCompanyId("region")}
                    required
                    rules={{ required: "Region is required" }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormInputText
                    name={"postCode"}
                    control={control}
                    setError={setError}
                    label={"Post Code"}
                    required
                    rules={{ required: "Post Code is required" }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormDate
                    name={"trialDay"}
                    control={control}
                    setError={setError}
                    type={"date"}
                    required
                    label={"Trial Day"}
                    rules={{ required: "Trial Day is required" }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormInputText
                    name={"phoneNumber"}
                    control={control}
                    setError={setError}
                    label={"Phone Number"}
                    rules={{
                      pattern: {
                        value: /^\d+$/,
                        message: "Phone number should only contain digits",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputText
                    required
                    name="address"
                    multiline
                    rows={3}
                    control={control}
                    setError={setError}
                    label="Address"
                    rules={{ required: "Address is required" }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }
);
